/**
 * SubcolumnProvider — the typed-aggregator extension point.
 *
 * The calendar-list aggregator doesn't know what kinds of rows live
 * underneath it. It iterates each constituent row and asks every
 * registered provider: "do you produce a subcolumn for this row?"
 * The first non-null response wins.
 *
 * Today there's one provider — `eventSubcolumnProvider`, which
 * handles event-shaped rows by delegating to `buildEventSubcolumn`.
 * Future per-shape renderers (tasks, weather, etc.) implement the
 * same interface and the aggregator picks them up automatically.
 *
 * Per permissive-renderer-architecture migration plan §Phase D
 * (final step — fully type-agnostic dispatch).
 */

import type { Option } from "@tento-lona/sheets";
import { type SheetRow } from "@tento-lona";
import type { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import {
  EventSubcolumn,
  type EventSubcolumnHost,
} from "./build-event-subcolumn.ts";

/** Generic provider contract. The host shape varies per renderer
 *  (event subcolumns need an events lookup; task subcolumns would
 *  need a task lookup, etc.); each provider declares its own. */
export interface SubcolumnProvider<H> {
  /**
   * Inspect `row` and return a subcolumn iff this provider handles
   * the row's cell shape, else null. Today's only provider always
   * returns non-null (it's event-shape-agnostic at the aggregator
   * gate); future providers add real shape-probe gating.
   */
  subcolumnFor(
    row: SheetRow.Joined.Handle,
    host: H,
  ): Option<CalendarSubcolumn>;
}

/**
 * The canonical event subcolumn provider. The aggregator constructs
 * a per-row `EventSubcolumnHost` (with the row-baked filter closure)
 * and passes it in. Future shape providers run in parallel and the
 * first match wins.
 */
export const eventSubcolumnProvider: SubcolumnProvider<EventSubcolumnHost> = {
  subcolumnFor(row, host) {
    return EventSubcolumn.build({ row, host });
  },
};
